import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CommonDraggableList } from '../../components/DraggableList';
import { FlashdealReorderDraggableComponent } from '../../components/DraggableList/DraggableItem/FlashdealReorderDraggableComponent';
import { Button } from '@mui/material';
import { BackButton } from '../../components/Buttons/BackButton';
import { CUSTOMER_APP_ROUTE } from '../../constants/routes';
import { IProduct } from '../../types/product';
import { useEffect, useState } from 'react';
import { CustomerAppService } from '../../api/services/customerAppService';
import { IUpdateProductsBySectionRequest } from '../../types/api/request/customerapprequest';
import { useDispatch } from 'react-redux';
import { setDisplayedProducts } from '../../store/Reducers/CustomerAppSlice';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { showAlertDialog } from '../../store/Reducers/AlertDialogSlice';

const sectionLabels: Record<string, string> = {
  flashdeals: 'Flash Deals',
  popular: 'Popular',
  festival: 'Festival',
  trending: 'Trending',
  newarrival: 'New Arrival',
};

export const SectionReorderSequence = () => {
  const { sectionSlug = '' } = useParams<{ sectionSlug: string }>();
  const location = useLocation();
  const data = location.state as IProduct[] | undefined;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [sequencedProducts, setSequencedProducts] = useState<IProduct[]>([]);

  const hasUnsavedChanges = sequencedProducts?.length > 0;
  const backRoute = `${CUSTOMER_APP_ROUTE}/${sectionSlug}`;
  const sectionLabel = sectionLabels[sectionSlug] ?? sectionSlug;

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      dispatch(
        showAlertDialog({
          title: 'Wait!',
          description: 'You have unsaved changes. Are you sure you want to leave?',
          confirmText: 'Discard',
          cancelText: 'Cancel',
          onConfirm: () => {
            navigate(backRoute);
          },
        }),
      );
    } else {
      navigate(backRoute);
    }
  };

  const onClickSave = async () => {
    try {
      const payload: IUpdateProductsBySectionRequest = {
        items: sequencedProducts?.map((x: IProduct, index) => ({
          sequence: index + 1,
          sku: x.sKU,
        })),
      };

      const updated = await CustomerAppService.updateProductsBySection(sectionSlug, payload);

      dispatch(setDisplayedProducts(updated));

      dispatch(
        showNotification({
          message: `Successfully updated ${sectionLabel}.`,
        }),
      );

      setSequencedProducts([]);
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to update ${sectionLabel}. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <BackButton onClick={handleBack} />
        <span style={{ fontSize: 12 }}>Back to {sectionLabel}</span>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 20,
          alignItems: 'center',
        }}
      >
        <div>
          <div>This is where you can reorder the sequence of {sectionLabel} products.</div>
          <div>Don't forget to save your changes.</div>
        </div>

        <Button variant="contained" onClick={onClickSave} disabled={!sequencedProducts?.length}>
          Save
        </Button>
      </div>

      <CommonDraggableList
        data={data}
        keyIdentifier="iD"
        DraggableComponent={FlashdealReorderDraggableComponent}
        onChangeSequence={(updatedSequence: []) => {
          setSequencedProducts(updatedSequence as IProduct[]);
        }}
      />
    </>
  );
};
